<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="CRM Quotations"
    title="Create Commercial Agreement"
    subtitle="Promote accepted quotation content into a contract draft while keeping quotation history intact.">
    <div nav class="conversion-tabs" role="tablist">
      @for (tab of tabs; track tab.id) {
        <button type="button" [class.active]="activeTab() === tab.id" (click)="setTab(tab.id)">
          <i [class]="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      }
    </div>
    <div actions>
      <p-button label="Refresh Preview" icon="fa-solid fa-rotate" severity="secondary" [outlined]="true" [loading]="loading()" (onClick)="loadPreview()"></p-button>
      <p-button label="Create Draft" icon="fa-solid fa-file-contract" [disabled]="!isRequestReady() || converting()" [loading]="converting()" (onClick)="convert()"></p-button>
    </div>
  </app-page-hero>

  @if (preview()?.quotation; as quote) {
    <app-page-data-panel title="Quotation Snapshot" [subtitle]="quote.subject" [resultText]="quote.status">
      <div class="snapshot-grid">
        <div><span>Quotation</span><b>{{ quote.quotationNumber || quote.id }}</b></div>
        <div><span>Customer</span><b>{{ quote.customerName || '-' }}</b></div>
        <div><span>Total</span><b>{{ quote.currencyCode || 'SAR' }} {{ quote.totalAmount || 0 | number:'1.2-2' }}</b></div>
        <div><span>Validity</span><b>{{ quote.startDate | date:'mediumDate' }} - {{ quote.expiryDate | date:'mediumDate' }}</b></div>
      </div>
    </app-page-data-panel>
  }

  @if (activeTab() === 'parties') {
    <app-page-form-section icon="fa-solid fa-building-user" title="Parties And Dates" subtitle="Select the contract target, parties, representatives, and effective dates for the draft revision.">
      <div class="form-grid">
        <label>
          <span>Conversion Mode</span>
          <p-select [options]="modeOptions" optionLabel="label" optionValue="value" [(ngModel)]="request.mode" (onChange)="onModeChange()" styleClass="w-full"></p-select>
        </label>
        @if (request.mode === 'AMENDMENT') {
          <label>
            <span>Target Contract</span>
            <p-select [options]="links()" optionLabel="contractNumber" optionValue="contractId" [(ngModel)]="request.targetContractId" styleClass="w-full"></p-select>
          </label>
        }
        <label>
          <span>Effective Start</span>
          <p-datePicker [(ngModel)]="request.effectiveStartDate" appendTo="body" styleClass="w-full"></p-datePicker>
        </label>
        <label>
          <span>Effective End</span>
          <p-datePicker [(ngModel)]="request.effectiveEndDate" appendTo="body" styleClass="w-full"></p-datePicker>
        </label>
        @if (request.mode !== 'AMENDMENT') {
          <label>
            <span>Seller Source</span>
            <p-select [options]="firstPartyModeOptions" optionLabel="label" optionValue="value"
              [(ngModel)]="firstPartyMode" (onChange)="onFirstPartyModeChanged($event.value)" styleClass="w-full"></p-select>
          </label>
          <label>
            <span>Seller Company</span>
            @if (firstPartyMode === 'SELF') {
              <input pInputText [value]="currentCompanyLabel()" readonly class="w-full p-disabled" />
            } @else {
              <p-select [options]="firstPartyCrmOptions" optionLabel="label" dataKey="key" [filter]="true" filterBy="label,companyLabel"
                [showClear]="true" [(ngModel)]="selectedFirstPartyCrmOption" (onChange)="onFirstPartyCrmOptionChanged($event.value)" styleClass="w-full"></p-select>
            }
          </label>
          <label>
            <span>Seller Representative</span>
            <p-select [options]="firstPartyReps" optionLabel="name" optionValue="id" [(ngModel)]="request.firstPartyRepId" styleClass="w-full"></p-select>
          </label>
          <label>
            <span>Buyer Source</span>
            <p-select [options]="secondPartyModeOptions" optionLabel="label" optionValue="value"
              [(ngModel)]="secondPartyMode" (onChange)="onSecondPartyModeChanged($event.value)" styleClass="w-full"></p-select>
          </label>
          <label>
            <span>Buyer Company</span>
            @if (secondPartyMode === 'SELF') {
              <input pInputText [value]="currentCompanyLabel()" readonly class="w-full p-disabled" />
            } @else {
              <p-select [options]="secondPartyCrmOptions" optionLabel="label" dataKey="key" [filter]="true" filterBy="label,companyLabel"
                [showClear]="true" [(ngModel)]="selectedSecondPartyCrmOption" (onChange)="onSecondPartyCrmOptionChanged($event.value)" styleClass="w-full"></p-select>
            }
          </label>
          <label>
            <span>Buyer Representative</span>
            <p-select [options]="secondPartyReps" optionLabel="name" optionValue="id" [(ngModel)]="request.secondPartyRepId" styleClass="w-full"></p-select>
          </label>
        }
      </div>

      @if (showBuyerLinkWarning()) {
        <div class="party-warning">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>The quotation client is not linked to a portal company. Choose a valid buyer before creating the draft.</span>
        </div>
      }

      @if (preview()?.overrideRequired) {
        <label class="full-width">
          <span>Override Reason</span>
          <textarea pTextarea [(ngModel)]="request.overrideReason" rows="3" class="w-full" placeholder="Required because this quotation is not in an approved conversion status."></textarea>
        </label>
      }
    </app-page-form-section>
  }

  @if (activeTab() === 'terms') {
    <app-page-data-panel title="Terms Promotion" subtitle="Quotation terms and pricing notes selected for contract term lineage." [resultText]="targetItems('CONTRACT_TERM').length + ' items'">
      <ng-container *ngTemplateOutlet="mappingTable; context: { $implicit: targetItems('CONTRACT_TERM') }"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'lines') {
    <app-page-data-panel title="Quotation Lines" subtitle="Choose whether each quotation line remains quote-only or becomes enforceable contract data." [resultText]="items().length + ' mapped items'">
      <ng-container *ngTemplateOutlet="mappingTable; context: { $implicit: items() }"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'rates') {
    <app-page-data-panel title="Rate Card Candidates" subtitle="Only selected rate-card candidates can create delivery rate cards on the draft revision." [resultText]="targetItems('RATE_CARD').length + ' candidates'">
      <ng-container *ngTemplateOutlet="mappingTable; context: { $implicit: targetItems('RATE_CARD') }"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'obligations') {
    <app-page-data-panel title="Obligation Candidates" subtitle="Stored as promotion lineage in this phase; obligation execution comes later." [resultText]="targetItems('OBLIGATION').length + ' candidates'">
      <ng-container *ngTemplateOutlet="mappingTable; context: { $implicit: targetItems('OBLIGATION') }"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'documents') {
    <app-page-data-panel title="Document Candidates" subtitle="Stored as promotion lineage in this phase; contract document linking comes later." [resultText]="targetItems('DOCUMENT').length + ' candidates'">
      <ng-container *ngTemplateOutlet="mappingTable; context: { $implicit: targetItems('DOCUMENT') }"></ng-container>
    </app-page-data-panel>
  }

  @if (activeTab() === 'warnings') {
    <app-page-data-panel title="Conversion Warnings" subtitle="Resolve errors before conversion; warnings are stored as conversion context." [resultText]="(preview()?.warnings?.length || 0) + ' warnings'">
      <div class="warning-list">
        @for (warning of preview()?.warnings || []; track warning.code) {
          <div class="warning-row">
            <p-tag [severity]="warningSeverity(warning.severity)" [value]="warning.severity || 'INFO'"></p-tag>
            <div>
              <b>{{ warning.code }}</b>
              <span>{{ warning.message }}</span>
            </div>
          </div>
        }
      </div>
    </app-page-data-panel>
  }

  @if (activeTab() === 'audit') {
    <app-page-data-panel title="Existing Contract Links" subtitle="Previously created contract drafts or amendments from this quotation." [resultText]="links().length + ' links'">
      <p-table [value]="links()" styleClass="p-datatable-sm p-datatable-gridlines">
        <ng-template pTemplate="header">
          <tr>
            <th>Contract</th>
            <th>Revision</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-link>
          <tr>
            <td><a [routerLink]="['/contracts/view', link.contractId]">{{ link.contractNumber || link.contractId }}</a></td>
            <td>{{ link.revisionNumber || link.revisionId || '-' }}</td>
            <td>{{ link.linkType }}</td>
            <td>{{ link.status }}</td>
            <td>{{ link.createdAt | date:'medium' }}</td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }
</app-page-shell>

<ng-template #mappingTable let-list>
  <p-table [value]="list" styleClass="p-datatable-sm p-datatable-gridlines">
    <ng-template pTemplate="header">
      <tr>
        <th style="width: 5rem">Use</th>
        <th>Source</th>
        <th style="width: 14rem">Target</th>
        <th>Warning</th>
        <th style="width: 10rem">Amount</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-item>
      <tr>
        <td><p-checkbox [(ngModel)]="item.selected" [binary]="true"></p-checkbox></td>
        <td>
          <div class="item-title">{{ item.label || item.key }}</div>
          <small>{{ item.sourceType }} {{ item.quotationLineId ? '#' + item.quotationLineId : '' }}</small>
        </td>
        <td>
          <p-select [options]="targetOptions" optionLabel="label" optionValue="value" [(ngModel)]="item.targetType" appendTo="body" styleClass="w-full"></p-select>
        </td>
        <td>{{ item.warningText || '-' }}</td>
        <td>{{ item.amount || '-' }}</td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr><td colspan="5">No mapped items in this category.</td></tr>
    </ng-template>
  </p-table>
</ng-template>
